import { apiService } from "./api.service";

class BlogService {
  // ============ PRIVATE HELPER METHODS ============

  /**
   * Get authentication token from cookie
   * @returns {string|null} Token if found, null otherwise
   */
  getAuthTokenFromCookie() {
    try {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "admin_access_token") {
          return value;
        }
      }
    } catch (error) {
      console.warn("Error reading auth token from cookie:", error);
    }
    return null;
  }

  // Public End-points
  getPublicRequestConfig(customConfig = {}) {
    return {
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...customConfig.headers,
      },
      ...customConfig,
    };
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated
   */

  isAuthenticated() {
    const token = this.getAuthTokenFromCookie();

    // If there's a token, consider authenticated
    if (token) {
      return true;
    }

    // Also check for user data as fallback
    const userStr = localStorage.getItem("admin_user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return !!(user && user.email);
      } catch {
        return false;
      }
    }

    return false;
  }

  /**
   * Ensure authentication before making request
   * @throws {Error} If not authenticated
   */
  ensureAuthenticated() {
    if (!this.isAuthenticated()) {
      throw new Error("Not authenticated. Please login first.");
    }
  }

  /**
   * Get request config with proper authentication
   * @param {Object} customConfig - Custom configuration
   * @returns {Object} Request configuration
   */
  getRequestConfig(customConfig = {}) {
    const token = this.getAuthTokenFromCookie();
    const config = {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...customConfig.headers,
      },
      ...customConfig,
    };

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  /**
   * Handle API errors and retry logic
   * @param {Function} requestFn - The API request function
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<any>} API response
   */
  async withRetry(requestFn, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        if (
          i === retries - 1 ||
          error?.response?.status === 401 ||
          error?.response?.status === 403 ||
          error?.response?.status === 404
        ) {
          throw error;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000),
        );
      }
    }
  }

  /**
   * Validate post data before sending to API
   * @param {Object|FormData} data - Post data (can be plain object or FormData)
   * @returns {Object} Validation result
   */
  validatePostData(data) {
    const errors = [];

    // Helper function to get value from either FormData or plain object
    const getValue = (key) => {
      if (data instanceof FormData) {
        const value = data.get(key);
        // FormData returns null for empty fields, convert to empty string for validation
        return value !== null ? value : "";
      }
      return data[key];
    };

    const title = getValue("title");
    const slug = getValue("slug");
    const content = getValue("content");
    const excerpt = getValue("excerpt");
    const author_id = getValue("author_id");

    if (!title || title.trim().length < 2) {
      errors.push("Post title must be at least 2 characters");
    }

    if (!slug || slug.trim().length < 2) {
      errors.push("Post slug must be at least 2 characters");
    }

    if (!content || content.trim().length < 10) {
      errors.push("Post content must be at least 10 characters");
    }

    if (!excerpt || excerpt.trim().length < 20) {
      errors.push("Post excerpt must be at least 20 characters");
    }

    if (!author_id) {
      errors.push("Author ID is required");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate category data before sending to API
   * @param {Object} data - Category data
   * @returns {Object} Validation result
   */

  validateCategoryData(data) {
    const errors = [];

    // Helper function to get value from either FormData or plain object
    const getValue = (key) => {
      if (data instanceof FormData) {
        return data.get(key);
      }
      return data[key];
    };

    const name = getValue("name");
    const slug = getValue("slug");
    const description = getValue("description");

    // Only validate if the fields exist (for partial updates)
    if (name !== null && name !== undefined) {
      if (!name || name.trim().length < 2) {
        errors.push("Category name must be at least 2 characters");
      }
    }

    if (slug !== null && slug !== undefined) {
      if (!slug || slug.trim().length < 2) {
        errors.push("Category slug must be at least 2 characters");
      }
    }

    if (description !== null && description !== undefined) {
      if (!description || description.trim().length < 10) {
        errors.push("Category description must be at least 10 characters");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
  /**
   * Validate tag data before sending to API
   * @param {Object} data - Tag data
   * @returns {Object} Validation result
   */
  validateTagData(data) {
    const errors = [];

    if (!data.name || data.name.trim().length < 2) {
      errors.push("Tag name must be at least 2 characters");
    }

    if (!data.slug || data.slug.trim().length < 2) {
      errors.push("Tag slug must be at least 2 characters");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate comment data before sending to API
   * @param {Object} data - Comment data
   * @returns {Object} Validation result
   */

  validateCommentData(data) {
    const errors = [];

    if (!data.content || data.content.trim().length < 2) {
      errors.push("Comment content must be at least 2 characters");
    }

    // If user is authenticated
    const isAuthenticated = this.isAuthenticated();

    // For non-authenticated users
    if (!isAuthenticated) {
      if (!data.author_name || data.author_name.trim().length < 2) {
        errors.push("Author name must be at least 2 characters");
      }

      if (!data.author_email || !this.isValidEmail(data.author_email)) {
        errors.push("Valid author email is required");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate newsletter input
   * @param {Object} data - Newsletter data
   * @returns {Object} Validation result
   */
  validateNewsletterInput(data) {
    const errors = [];

    if (!data.email || !this.isValidEmail(data.email)) {
      errors.push("Valid email is required");
    }

    if (data.name && data.name.trim().length < 2) {
      errors.push("Name must be at least 2 characters");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate comment reaction input
   * @param {Object} data - Comment reaction data
   * @returns {Object} Validation result
   */
  validateCommentReactionInput(data) {
    const errors = [];

    if (typeof data.is_like !== "boolean" && data.is_like !== undefined) {
      errors.push("is_like must be a boolean value");
    }

    if (data.session_id && typeof data.session_id !== "string") {
      errors.push("session_id must be a string");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate email format
   * @param {string} email - Email address
   * @returns {boolean} True if valid email
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Convert image data to displayable URL
   * @param {Object|string|ArrayBuffer|Uint8Array} imageData - Image data from API
   * @param {string} mimeType - MIME type of the image
   * @returns {string|null} Image URL or null
   */
  getImageUrl = (imageData, mimeType = "image/jpeg") => {
    if (!imageData) return null;

    // If it's already a URL string
    if (typeof imageData === "string" && imageData.startsWith("http")) {
      return imageData;
    }

    // If it's already a data URL
    if (typeof imageData === "string" && imageData.startsWith("data:image")) {
      return imageData;
    }

    // If it's an object with data property (common API response format)
    if (imageData && typeof imageData === "object") {
      try {
        // Handle { type: 'Buffer', data: [...] } format
        if (imageData.type === "Buffer" && Array.isArray(imageData.data)) {
          const uint8Array = new Uint8Array(imageData.data);
          const binaryString = Array.from(uint8Array)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          return `data:${mimeType};base64,${btoa(binaryString)}`;
        }

        // Handle ArrayBuffer or Uint8Array
        if (imageData instanceof ArrayBuffer) {
          const uint8Array = new Uint8Array(imageData);
          const binaryString = Array.from(uint8Array)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          return `data:${mimeType};base64,${btoa(binaryString)}`;
        }

        // Handle Uint8Array
        if (imageData instanceof Uint8Array) {
          const binaryString = Array.from(imageData)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          return `data:${mimeType};base64,${btoa(binaryString)}`;
        }

        // Handle plain object with data array
        if (imageData.data && Array.isArray(imageData.data)) {
          const uint8Array = new Uint8Array(imageData.data);
          const binaryString = Array.from(uint8Array)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          return `data:${mimeType};base64,${btoa(binaryString)}`;
        }
      } catch (error) {
        return null;
      }
    }

    // If it's a plain base64 string
    if (typeof imageData === "string") {
      // Check if it's already base64 (no special chars except +/=)
      if (/^[A-Za-z0-9+/=]+$/.test(imageData)) {
        return `data:${mimeType};base64,${imageData}`;
      }
      // If it's a regular string but not URL, try to encode it
      try {
        return `data:${mimeType};base64,${btoa(imageData)}`;
      } catch {
        return null;
      }
    }

    return null;
  };

  /**
   * Format post data for API based on new schema
   * @param {Object} uiData - UI post data
   * @returns {Object} API-ready post data
   */
  formatPostForAPI(uiData) {
    // FIXED: Removed reference to undefined apiData variable
    const apiData = {
      title: uiData.title?.trim() || "",
      slug: uiData.slug?.trim() || "",
      content: uiData.content?.trim() || "",
      excerpt: uiData.excerpt?.trim() || "",
      content_html: uiData.content_html?.trim() || null,
      author_id: uiData.author_id || null,
      category_id: uiData.category_id || null,
      status: uiData.status || "draft",
      featured_image: uiData.featured_image || null,
      image_mime_type: uiData.image_mime_type || "image/jpeg",
      image_size: uiData.image_size || 0,
      image_width: uiData.image_width || null,
      image_height: uiData.image_height || null,
      image_alt: uiData.image_alt || "",
      image_caption: uiData.image_caption || "",
      meta_title: uiData.meta_title?.trim() || "",
      meta_description: uiData.meta_description?.trim() || "",
      meta_keywords: uiData.meta_keywords?.trim() || "",
      canonical_url: uiData.canonical_url || null,
      og_image: uiData.og_image || null,
      og_title: uiData.og_title || null,
      og_description: uiData.og_description || null,
      twitter_card: uiData.twitter_card || null,
      schema_markup: uiData.schema_markup || null,
      is_featured: Boolean(uiData.is_featured),
      is_pinned: Boolean(uiData.is_pinned),
      is_commentable: Boolean(uiData.is_commentable),
      is_indexable: Boolean(uiData.is_indexable),
      is_amp_enabled: Boolean(uiData.is_amp_enabled),
      is_podcast: Boolean(uiData.is_podcast),
      podcast_duration: uiData.podcast_duration || null,
      podcast_file_url: uiData.podcast_file_url || null,
      podcast_file_size: uiData.podcast_file_size || null,
      published_at: uiData.published_at || null,
      scheduled_at: uiData.scheduled_at || null,
      expires_at: uiData.expires_at || null,
      tags: Array.isArray(uiData.tags) ? uiData.tags : [],
      related_post_ids: uiData.related_post_ids || [],
      custom_fields: uiData.custom_fields || {},
      template: uiData.template || "default",
      custom_css: uiData.custom_css || null,
      custom_js: uiData.custom_js || null,
      word_count: uiData.word_count || 0,
      reading_time: uiData.reading_time || 0,
      version: uiData.version || 1,
    };

    Object.keys(apiData).forEach((key) => {
      if (apiData[key] === undefined) {
        delete apiData[key];
      }
    });

    return apiData;
  }

  /**
   * Format API response for UI display based on new schema
   * @param {Object} apiData - API post data
   * @returns {Object} UI-ready post data
   */
  formatPostForUI = (apiData) => {
    const featuredImageUrl = this.getImageUrl(
      apiData.featured_image,
      apiData.image_mime_type || "image/jpeg",
    );

    const ogImageUrl = this.getImageUrl(
      apiData.og_image,
      apiData.image_mime_type || "image/jpeg",
    );

    return {
      id: apiData.id,
      title: apiData.title,
      slug: apiData.slug,
      content: apiData.content || "",
      content_html: apiData.content_html || "",
      excerpt: apiData.excerpt || "",
      author_id: apiData.author_id,
      author: apiData.author || {},
      category_id: apiData.category_id,
      category: apiData.category || {},
      status: apiData.status || "draft",
      featured_image: featuredImageUrl,
      image_mime_type: apiData.image_mime_type || "image/jpeg",
      image_size: apiData.image_size || 0,
      image_width: apiData.image_width || null,
      image_height: apiData.image_height || null,
      image_alt: apiData.image_alt || "",
      image_caption: apiData.image_caption || "",
      meta_title: apiData.meta_title || "",
      meta_description: apiData.meta_description || "",
      meta_keywords: apiData.meta_keywords || "",
      canonical_url: apiData.canonical_url || null,
      og_image: ogImageUrl,
      og_title: apiData.og_title || "",
      og_description: apiData.og_description || "",
      twitter_card: apiData.twitter_card || null,
      schema_markup: apiData.schema_markup || null,
      is_featured: Boolean(apiData.is_featured),
      is_pinned: Boolean(apiData.is_pinned),
      is_commentable: Boolean(apiData.is_commentable),
      is_indexable: Boolean(apiData.is_indexable),
      is_amp_enabled: Boolean(apiData.is_amp_enabled),
      is_podcast: Boolean(apiData.is_podcast),
      podcast_duration: apiData.podcast_duration || null,
      podcast_file_url: apiData.podcast_file_url || null,
      podcast_file_size: apiData.podcast_file_size || null,
      view_count: parseInt(apiData.view_count) || 0,
      unique_view_count: parseInt(apiData.unique_view_count) || 0,
      like_count: parseInt(apiData.like_count) || 0,
      share_count: parseInt(apiData.share_count) || 0,
      comment_count: parseInt(apiData.comment_count) || 0,
      bookmark_count: parseInt(apiData.bookmark_count) || 0,
      clap_count: parseInt(apiData.clap_count) || 0,
      tags: apiData.tags || [],
      social_shares: apiData.social_shares || {},
      related_post_ids: apiData.related_post_ids || [],
      custom_fields: apiData.custom_fields || {},
      template: apiData.template || "default",
      custom_css: apiData.custom_css || null,
      custom_js: apiData.custom_js || null,
      published_at: apiData.published_at,
      scheduled_at: apiData.scheduled_at,
      expires_at: apiData.expires_at,
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
      deleted_at: apiData.deleted_at || null,
      published_version: apiData.published_version || 0,
      created_by: apiData.created_by || null,
      updated_by: apiData.updated_by || null,
      deleted_by: apiData.deleted_by || null,
      version: apiData.version || 1,
      word_count: apiData.word_count || 0,
      reading_time: apiData.reading_time || 0,
      seo_score: apiData.seo_score || 0,
      readability_score: apiData.readability_score || 0,

      author_name: apiData.author_name || "",
      author_email: apiData.author_email || "",
      author_avatar: apiData.author_avatar || null,
      category_name: apiData.category_name || "",
      category_slug: apiData.category_slug || "",
      category_color: apiData.custom_fields?.category_color || null,
    };
  };

  /**
   * Format category data for API based on new schema
   * @param {Object} uiData - UI category data
   * @returns {Object} API-ready category data
   */
  formatCategoryForAPI(uiData) {
    const apiData = {
      name: uiData.name?.trim() || "",
      slug: uiData.slug?.trim() || "",
      description: uiData.description?.trim() || null,
      featured_image: uiData.featured_image || null,
      image_mime_type: uiData.image_mime_type || "image/jpeg",
      image_size: uiData.image_size || 0,
      image_width: uiData.image_width ?? null,
      image_height: uiData.image_height ?? null,
      parent_id: uiData.parent_id || null,
      display_order: uiData.display_order || 0,
      is_active: Boolean(uiData.is_active),
      is_nav_visible: Boolean(uiData.is_nav_visible),
      show_in_homepage: Boolean(uiData.show_in_homepage),
      meta_title: uiData.meta_title?.trim() || null,
      meta_description: uiData.meta_description?.trim() || null,
      meta_keywords: uiData.meta_keywords?.trim() || null,
      og_image: uiData.og_image || null,
      og_title: uiData.og_title?.trim() || null,
      og_description: uiData.og_description?.trim() || null,
      custom_css: uiData.custom_css || null,
      custom_js: uiData.custom_js || null,
      layout_template: uiData.layout_template || "default",
      color_scheme: uiData.color_scheme || null,
      version: uiData.version || 1,
    };

    // Remove undefined values
    Object.keys(apiData).forEach((key) => {
      if (apiData[key] === undefined) {
        delete apiData[key];
      }
    });

    return apiData;
  }

  /**
   * Format API response for UI display
   * @param {Object} apiData - API category data
   * @returns {Object} UI-ready category data
   */
  formatCategoryForUI = (apiData) => {
    const featuredImageUrl = this.getImageUrl(
      apiData.featured_image,
      apiData.image_mime_type || "image/jpeg",
    );

    const ogImageUrl = this.getImageUrl(
      apiData.og_image,
      apiData.image_mime_type || "image/jpeg",
    );

    return {
      id: apiData.id,
      name: apiData.name,
      slug: apiData.slug,
      description: apiData.description,
      featured_image: featuredImageUrl,
      og_image: ogImageUrl,
      image_mime_type: apiData.image_mime_type || "image/jpeg",
      image_size: apiData.image_size || 0,
      image_width: apiData.image_width || null,
      image_height: apiData.image_height || null,
      parent_id: apiData.parent_id,
      display_order: apiData.display_order || 0,
      post_count: parseInt(apiData.post_count) || 0,
      published_post_count: parseInt(apiData.published_post_count) || 0,
      is_active: Boolean(apiData.is_active),
      is_nav_visible: Boolean(apiData.is_nav_visible),
      show_in_homepage: Boolean(apiData.show_in_homepage),
      meta_title: apiData.meta_title || "",
      meta_description: apiData.meta_description || "",
      meta_keywords: apiData.meta_keywords || "",
      custom_css: apiData.custom_css || null,
      custom_js: apiData.custom_js || null,
      layout_template: apiData.layout_template || "default",
      color_scheme: apiData.color_scheme || null,
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
      deleted_at: apiData.deleted_at || null,
      created_by: apiData.created_by || null,
      updated_by: apiData.updated_by || null,
      deleted_by: apiData.deleted_by || null,
      version: apiData.version || 1,
      children: apiData.children || [],
      parent: apiData.parent || null,
    };
  };

  /**
   * Format tag data for API based on new schema
   * @param {Object} uiData - UI tag data
   * @returns {Object} API-ready tag data
   */
  formatTagForAPI(uiData) {
    return {
      name: uiData.name?.trim() || "",
      slug: uiData.slug?.trim() || "",
      description: uiData.description?.trim() || "",
      meta_title: uiData.meta_title?.trim() || "",
      meta_description: uiData.meta_description?.trim() || "",
      is_featured: Boolean(uiData.is_featured),
    };
  }

  /**
   * Format API response for UI display
   * @param {Object} apiData - API tag data
   * @returns {Object} UI-ready tag data
   */
  formatTagForUI = (apiData) => {
    return {
      id: apiData.id,
      name: apiData.name,
      slug: apiData.slug,
      description: apiData.description || "",
      post_count: parseInt(apiData.post_count) || 0,
      meta_title: apiData.meta_title || "",
      meta_description: apiData.meta_description || "",
      is_featured: Boolean(apiData.is_featured),
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
    };
  };

  /**
   * Format comment data for API based on new schema
   * @param {Object} uiData - UI comment data
   * @returns {Object} API-ready comment data
   */
  formatCommentForAPI(uiData) {
    return {
      content: uiData.content?.trim() || "",
      author_name: uiData.author_name?.trim() || "",
      author_email: uiData.author_email?.trim() || "",
      author_website: uiData.author_website?.trim() || "",
      author_avatar: uiData.author_avatar || null,
      post_id: uiData.post_id || null,
      parent_id: uiData.parent_id || null,
      rating: uiData.rating || null,
      user_agent: uiData.user_agent || null,
      referrer: uiData.referrer || null,
      metadata: uiData.metadata || {},
    };
  }

  /**
   * Format API response for UI display based on new schema
   * @param {Object} apiData - API comment data
   * @returns {Object} UI-ready comment data
   */
  formatCommentForUI = (apiData) => {
    const authorAvatarUrl = this.getImageUrl(
      apiData.author_avatar,
      "image/jpeg",
    );

    return {
      id: apiData.id,
      content: apiData.content,
      content_html: apiData.content_html || "",
      author_name: apiData.author_name,
      author_email: apiData.author_email,
      author_website: apiData.author_website || "",
      author_avatar: authorAvatarUrl,
      author_ip: apiData.author_ip || "",
      guest_session_id: apiData.guest_session_id || null,
      user_agent: apiData.user_agent || "",
      referrer: apiData.referrer || "",
      post_id: apiData.post_id,
      parent_id: apiData.parent_id,
      thread_path: apiData.thread_path || "",
      depth: apiData.depth || 0,
      rating: apiData.rating || null,
      is_approved: Boolean(apiData.is_approved),
      is_spam: Boolean(apiData.is_spam),
      is_edited: Boolean(apiData.is_edited),
      is_highlighted: Boolean(apiData.is_highlighted),
      is_author_response: Boolean(apiData.is_author_response),
      likes: parseInt(apiData.likes) || 0,
      dislikes: parseInt(apiData.dislikes) || 0,
      replies_count: parseInt(apiData.replies_count) || 0,
      reports_count: parseInt(apiData.reports_count) || 0,
      spam_score: parseInt(apiData.spam_score) || 0,
      sentiment_score: apiData.sentiment_score || null,
      moderation_notes: apiData.moderation_notes || null,
      moderated_by: apiData.moderated_by || null,
      moderated_at: apiData.moderated_at || null,
      metadata: apiData.metadata || {},
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
      deleted_at: apiData.deleted_at || null,
      created_by: apiData.created_by || null,
      updated_by: apiData.updated_by || null,
      deleted_by: apiData.deleted_by || null,
      version: apiData.version || 1,
      post_title: apiData.post_title || "",
      author: apiData.author || null,
    };
  };

  /**
   * Format view data for API
   * @param {string} postId - Post ID
   * @param {Object} options - View options
   * @returns {Object} Formatted view data
   */
  formatViewData(postId, options = {}) {
    const sessionId = this.generateSessionId();
    const userId = this.getUserId();

    return {
      post_id: postId,
      session_id: sessionId,
      user_id: userId,
      ip_address: options.ipAddress || "0.0.0.0",
      user_agent: options.userAgent || navigator.userAgent,
      country: options.country || null,
      region: options.region || null,
      city: options.city || null,
      latitude: options.latitude || null,
      longitude: options.longitude || null,
      device_type: this.detectDeviceType(),
      device_vendor: options.deviceVendor || null,
      device_model: options.deviceModel || null,
      browser: this.detectBrowser(),
      browser_version: options.browserVersion || null,
      os: this.detectOS(),
      os_version: options.osVersion || null,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      referrer: document.referrer || null,
      referrer_source: this.detectReferrerSource(),
      utm_source: this.getUrlParameter("utm_source"),
      utm_medium: this.getUrlParameter("utm_medium"),
      utm_campaign: this.getUrlParameter("utm_campaign"),
      utm_term: this.getUrlParameter("utm_term"),
      utm_content: this.getUrlParameter("utm_content"),
      duration_seconds: options.durationSeconds || null,
      scroll_depth: options.scrollDepth || null,
      is_bounce: null,
      is_unique: true,
      exit_intent: null,
    };
  }

  /**
   * Format analytics date range
   * @param {Object} dateRange - Date range object
   * @returns {Object} Formatted date range
   */
  formatDateRange(dateRange) {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return null;
    }

    return {
      from:
        dateRange.start instanceof Date
          ? dateRange.start.toISOString()
          : dateRange.start,
      to:
        dateRange.end instanceof Date
          ? dateRange.end.toISOString()
          : dateRange.end,
    };
  }

  /**
   * Get current user name
   * @returns {string|null} User name or null
   */
  getUserName() {
    try {
      const userStr = localStorage.getItem("admin_user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.full_name || user.name || user.username || null;
      }
    } catch (error) {
      console.warn("Error getting user name:", error);
    }
    return null;
  }

  /**
   * Get current user email
   * @returns {string|null} User email or null
   */
  getUserEmail() {
    try {
      const userStr = localStorage.getItem("admin_user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.email || null;
      }
    } catch (error) {
      console.warn("Error getting user email:", error);
    }
    return null;
  }

  // ============ COMMENT REACTION METHODS ============

  /**
   * Toggle comment like/dislike
   * @param {string} commentId - Comment ID
   * @param {Object} data - Reaction data
   * @returns {Promise<Object>} API response
   */
  async toggleCommentLike(commentId, data = {}) {
    try {
      const config = this.getPublicRequestConfig();
      const sessionId = this.generateSessionId();
      const userId = this.getUserId();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/comments/${commentId}/reactions`,
          {
            session_id: sessionId,
            user_id: userId,
            is_like: data.is_like !== undefined ? data.is_like : true,
            ...data,
          },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle comment reaction",
        message: error.message,
        data: { liked: false, likes: 0, dislikes: 0 },
      };
    }
  }

  /**
   * Get comment reaction for current user
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} API response
   */
  async getCommentReaction(commentId) {
    try {
      const sessionId = this.generateSessionId();
      const userId = this.getUserId();

      const config = this.getPublicRequestConfig({
        params: {
          session_id: sessionId,
          user_id: userId,
        },
      });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/comments/${commentId}/reactions/me`, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to get comment reaction",
        message: error.message,
        data: { is_like: null },
      };
    }
  }

  /**
   * Get comment reactions statistics
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} API response
   */
  async getCommentReactionsStats(commentId) {
    try {
      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/blog/comments/${commentId}/reactions/stats`, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to get comment reactions stats",
        message: error.message,
        data: { likes: 0, dislikes: 0, total: 0 },
      };
    }
  }

  // ============ NEWSLETTER METHODS ============

  /**
   * Subscribe to newsletter
   * @param {Object} data - Subscription data
   * @returns {Promise<Object>} API response
   */
  async subscribeToNewsletter(data) {
    try {
      const validation = this.validateNewsletterInput(data);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/newsletter/subscribe", data, config),
      );

      // Handle the response format you provided
      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: response.data.message || "Subscription processed",
          already_subscribed: response.data.already_subscribed || false,
          subscription_id: response.data.subscription_id,
        };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to subscribe to newsletter",
        message: error.message,
      };
    }
  }

  /**
   * Confirm newsletter subscription
   * @param {string} token - Confirmation token
   * @returns {Promise<Object>} API response
   */
  async confirmNewsletterSubscription(token) {
    try {
      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/blog/newsletter/confirm/${token}`, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to confirm newsletter subscription",
        message: error.message,
      };
    }
  }

  /**
   * Unsubscribe from newsletter
   * @param {string} emailOrToken - Email or unsubscribe token
   * @param {Object} data - Optional data with reason
   * @returns {Promise<Object>} API response
   */
  async unsubscribeFromNewsletter(emailOrToken, data = {}) {
    try {
      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/newsletter/unsubscribe/${encodeURIComponent(emailOrToken)}`,
          data,
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to unsubscribe from newsletter",
        message: error.message,
      };
    }
  }

  // In your blogService.js file, update these methods:

  /**
   * Get newsletter subscribers (admin only)
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response
   */
  async getNewsletterSubscribers(params = {}) {
    try {
      this.ensureAuthenticated();

      // Build query parameters correctly
      const queryParams = {};

      if (params.page) queryParams.page = params.page;
      if (params.limit) queryParams.limit = params.limit;
      if (params.is_active !== undefined)
        queryParams.is_active = params.is_active;
      if (params.is_confirmed !== undefined)
        queryParams.is_confirmed = params.is_confirmed;
      if (params.source) queryParams.source = params.source;
      if (params.search) queryParams.search = params.search; // or queryParams.q = params.search

      if (params.date_range && typeof params.date_range === "object") {
        queryParams.date_range = JSON.stringify(params.date_range);
      }

      const config = this.getRequestConfig({ params: queryParams });
      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/newsletter/subscribers", config),
      );

      // Handle the response based on your API structure
      if (response.data) {
        // If response.data has success property
        if (response.data.success !== undefined) {
          return response.data;
        }

        // If response.data has data property (paginated)
        if (response.data.data !== undefined) {
          return {
            success: true,
            data: response.data,
          };
        }

        // If response.data is the array directly
        if (Array.isArray(response.data)) {
          return {
            success: true,
            data: {
              data: response.data,
              meta: {
                pagination: {
                  total: response.data.length,
                  page: 1,
                  limit: response.data.length,
                },
              },
            },
          };
        }

        // If response.data is an object with subscribers
        if (response.data.subscribers) {
          return {
            success: true,
            data: {
              data: response.data.subscribers,
              meta: response.data.meta || {
                pagination: {
                  total: response.data.subscribers.length,
                },
              },
            },
          };
        }

        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        message: "No data received from server",
      };
    } catch (error) {
      // Return a structured error response
      return {
        success: false,
        error: error.message,
        message:
          error.response?.data?.message ||
          "Failed to get newsletter subscribers",
        status: error.response?.status,
        data: {
          data: [],
          meta: { pagination: { total: 0 } },
        },
      };
    }
  }

  /**
   * Get newsletter statistics (admin only)
   * @returns {Promise<Object>} API response
   */
  async getNewsletterStats() {
    try {
      this.ensureAuthenticated();

      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/newsletter/stats", config),
      );

      // Handle the response based on your API structure
      if (response.data) {
        // If response.data has success property
        if (response.data.success !== undefined) {
          return response.data;
        }

        // If response.data has data property
        if (response.data.data !== undefined) {
          return {
            success: true,
            data: response.data.data,
          };
        }

        // If response.data is the stats object directly
        return {
          success: true,
          data: response.data,
        };
      }

      // Fallback to default stats
      return {
        success: true,
        data: {
          total_subscribers: 0,
          active_subscribers: 0,
          pending_subscribers: 0,
          total_campaigns: 0,
        },
      };
    } catch (error) {
      // Return default stats on error
      return {
        success: false,
        error: error.message,
        message:
          error.response?.data?.message || "Failed to get newsletter stats",
        data: {
          total_subscribers: 0,
          active_subscribers: 0,
          pending_subscribers: 0,
          total_campaigns: 0,
        },
      };
    }
  }

  // ============ PERFORMANCE & MAINTENANCE METHODS ============

  /**
   * Get performance metrics (admin only)
   * @returns {Promise<Object>} API response
   */
  async getPerformanceMetrics() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/performance", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to get performance metrics",
        message: error.message,
        data: {
          load_time: 0,
          time_to_first_byte: 0,
          server_response_time: 0,
          cache_hit_rate: 0,
        },
      };
    }
  }

  /**
   * Clean up old data (admin only)
   * @param {Object} params - Cleanup parameters
   * @returns {Promise<Object>} API response
   */
  async cleanupOldData(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/cleanup", {}, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to cleanup old data",
        message: error.message,
        data: { deleted_views: 0, deleted_analytics: 0 },
      };
    }
  }

  /**
   * Sync tags from JSON (admin only)
   * @returns {Promise<Object>} API response
   */
  async syncTagsFromJson() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/sync-tags", {}, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to sync tags from JSON",
        message: error.message,
        data: { synced_posts: 0, synced_tags: 0 },
      };
    }
  }

  // ============ ENHANCED SEARCH METHOD ============

  /**
   * Enhanced search across posts, categories, tags, and comments
   * @param {string} query - Search query
   * @param {Object} params - Search parameters
   * @returns {Promise<Object>} API response
   */
  async search(query, params = {}) {
    try {
      const { include_types = ["posts"], page = 1, limit = 10 } = params;
      const includeTypesStr = Array.isArray(include_types)
        ? include_types.join(",")
        : include_types;

      const config = this.getPublicRequestConfig({
        params: {
          q: query,
          include_types: includeTypesStr,
          page,
          limit,
          ...params,
        },
      });

      const response = await this.withRetry(() =>
        apiService.get("/blog/search", config),
      );

      if (response.data) {
        const results = response.data;

        if (results.posts && Array.isArray(results.posts)) {
          results.posts = results.posts.map(this.formatPostForUI.bind(this));
        }

        if (results.categories && Array.isArray(results.categories)) {
          results.categories = results.categories.map(
            this.formatCategoryForUI.bind(this),
          );
        }

        if (results.tags && Array.isArray(results.tags)) {
          results.tags = results.tags.map(this.formatTagForUI.bind(this));
        }

        if (results.comments && Array.isArray(results.comments)) {
          results.comments = results.comments.map(
            this.formatCommentForUI.bind(this),
          );
        }
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to search",
        message: error.message,
        data: {
          posts: [],
          categories: [],
          tags: [],
          comments: [],
          total_results: 0,
          suggestions: [],
        },
      };
    }
  }

  // ============ FEED & SITEMAP METHODS ============

  /**
   * Generate blog feed
   * @param {Object} params - Feed parameters
   * @returns {Promise<Object>} API response
   */
  async generateFeed(params = {}) {
    try {
      const config = this.getPublicRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get("/blog/feed", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to generate feed",
        message: error.message,
      };
    }
  }

  /**
   * Generate sitemap data
   * @returns {Promise<Object>} API response
   */
  async generateSitemap() {
    try {
      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/blog/sitemap", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to generate sitemap",
        message: error.message,
      };
    }
  }

  // ============ UTILITY ENDPOINTS ============

  /**
   * Get system health status
   * @returns {Promise<Object>} API response
   */
  async getHealthStatus() {
    try {
      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/blog/health", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to get health status",
        message: error.message,
      };
    }
  }

  /**
   * Get rate limiting information
   * @returns {Promise<Object>} API response
   */
  async getRateLimits() {
    try {
      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/blog/rate-limits", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to get rate limits",
        message: error.message,
        data: {
          public: { requests_per_minute: 60, requests_per_hour: 1000 },
          authenticated: { requests_per_minute: 120, requests_per_hour: 5000 },
          admin: { requests_per_minute: 300, requests_per_hour: 10000 },
        },
      };
    }
  }

  // ============ BATCH OPERATION METHODS ============

  /**
   * Bulk delete posts (admin only)
   * @param {Array<string>} postIds - Array of post IDs to delete
   * @returns {Promise<Object>} API response
   */
  async bulkDeletePosts(postIds) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/posts/batch/delete", { postIds }, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk delete posts",
        message: error.message,
        data: { deleted_count: 0 },
      };
    }
  }

  /**
   * Bulk publish posts (admin only)
   * @param {Array<string>} postIds - Array of post IDs to publish
   * @returns {Promise<Object>} API response
   */
  async bulkPublishPosts(postIds) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/posts/batch/publish", { postIds }, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk publish posts",
        message: error.message,
        data: { published_count: 0 },
      };
    }
  }

  /**
   * Bulk feature/unfeature posts (admin only)
   * @param {Array<string>} postIds - Array of post IDs
   * @param {boolean} featured - Whether to feature or unfeature
   * @returns {Promise<Object>} API response
   */
  async bulkFeaturePosts(postIds, featured = true) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          "/blog/admin/posts/batch/feature",
          { postIds, featured },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk feature posts",
        message: error.message,
        data: { updated_count: 0, featured },
      };
    }
  }

  // ============ EXPORT/IMPORT METHODS ============

  /**
   * Export posts (admin only)
   * @param {Object} params - Export parameters
   * @returns {Promise<Object>} API response
   */
  async exportPosts(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/export/posts", params, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to export posts",
        message: error.message,
      };
    }
  }

  /**
   * Import posts (admin only)
   * @param {FormData} formData - Form data with import file
   * @returns {Promise<Object>} API response
   */
  async importPosts(formData) {
    try {
      this.ensureAuthenticated();
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${this.getAuthTokenFromCookie()}`,
        },
        withCredentials: true,
      };

      const response = await this.withRetry(() =>
        apiService.instance.post("/blog/admin/import/posts", formData, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to import posts",
        message: error.message,
      };
    }
  }

  // ============ CACHE MANAGEMENT METHODS ============

  /**
   * Clear cache (admin only)
   * @param {Object} params - Cache parameters
   * @returns {Promise<Object>} API response
   */
  async clearCache(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/cache/clear", params, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to clear cache",
        message: error.message,
      };
    }
  }

  /**
   * Get cache statistics (admin only)
   * @returns {Promise<Object>} API response
   */
  async getCacheStats() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/cache/stats", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to get cache stats",
        message: error.message,
        data: { total_keys: 0, memory_usage: "0 MB", hit_rate: 0 },
      };
    }
  }

  // ============ WEBHOOK METHODS ============

  /**
   * Handle Medium webhook (admin only)
   * @param {Object} data - Webhook data
   * @returns {Promise<Object>} API response
   */
  async handleMediumWebhook(data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/webhooks/medium", data, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to handle Medium webhook",
        message: error.message,
      };
    }
  }

  /**
   * Handle WordPress webhook (admin only)
   * @param {Object} data - Webhook data
   * @returns {Promise<Object>} API response
   */
  async handleWordPressWebhook(data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/webhooks/wordpress", data, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to handle WordPress webhook",
        message: error.message,
      };
    }
  }

  // ============ EXISTING PUBLIC BLOG POSTS ENDPOINTS ============

  async getPublishedPosts(params = {}) {
    try {
      const processedParams = { ...params };
      if (params.exclude_ids && Array.isArray(params.exclude_ids)) {
        processedParams.exclude_ids = params.exclude_ids.join(",");
      }
      if (params.date_range && typeof params.date_range === "object") {
        processedParams.date_range = JSON.stringify(params.date_range);
      }
      if (params.status && Array.isArray(params.status)) {
        // Handle multiple statuses - API expects status to be an array in params
        processedParams.status = params.status;
      }

      const config = this.getRequestConfig({ params: processedParams });

      const response = await this.withRetry(() =>
        apiService.get("/blog/posts", config),
      );

      // Handle new paginated response format
      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatPostForUI.bind(this),
        );
      } else if (response.data?.posts || response.data?.items) {
        const posts = response.data.posts || response.data.items;
        response.data = {
          ...response.data,
          posts: posts.map(this.formatPostForUI.bind(this)),
        };
      } else if (Array.isArray(response.data)) {
        response.data = response.data.map(this.formatPostForUI.bind(this));
      }

      return this.parsePaginatedResponse(response);
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch published posts",
        message: error.message,
        status: error.response?.status,
        data: [],
        meta: { pagination: { total: 0 } },
      };
    }
  }

  async getFeaturedPosts(limit = 5, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { limit, ...params },
      });

      const response = await this.withRetry(() =>
        apiService.get("/blog/posts/featured", config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch featured posts",
        message: error.message,
        data: [],
      };
    }
  }

  async getRecentPosts(limit = 5, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { limit, ...params },
      });

      const response = await this.withRetry(() =>
        apiService.get("/blog/posts/recent", config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch recent posts",
        message: error.message,
        data: [],
      };
    }
  }

  async getPopularPosts(limit = 5, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { limit, ...params },
      });

      const response = await this.withRetry(() =>
        apiService.get("/blog/posts/popular", config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch popular posts",
        message: error.message,
        data: [],
      };
    }
  }

  async getTrendingPosts(limit = 5, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { limit, ...params },
      });

      const response = await this.withRetry(() =>
        apiService.get("/blog/posts/trending", config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch trending posts",
        message: error.message,
        data: [],
      };
    }
  }

  async searchPosts(query, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { q: query, ...params },
      });

      const response = await this.withRetry(() =>
        apiService.get("/blog/posts/search", config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to search posts",
        message: error.message,
        data: [],
      };
    }
  }

  async getPostsByTag(tag, params = {}) {
    try {
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/tag/${encodeURIComponent(tag)}`, config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch posts by tag",
        message: error.message,
        data: [],
      };
    }
  }

  async getPostsByCategory(categoryId, params = {}) {
    try {
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/category/id/${categoryId}`, config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch posts by category",
        message: error.message,
        data: [],
      };
    }
  }

  async getPostsByCategorySlug(slug, params = {}) {
    try {
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get(
          `/blog/posts/category/${encodeURIComponent(slug)}`,
          config,
        ),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch posts by category slug",
        message: error.message,
        data: [],
      };
    }
  }

  async getPostsByAuthor(authorId, params = {}) {
    try {
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/author/${authorId}`, config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch posts by author",
        message: error.message,
        data: [],
      };
    }
  }

  async getPostBySlug(slug) {
    try {
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/slug/${encodeURIComponent(slug)}`, config),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch post by slug",
        message: error.message,
      };
    }
  }

  async getPostReactions(postId) {
    try {
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/${postId}/reactions`, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch post reactions",
        message: error.message,
        data: {
          likes: 0,
          claps: 0,
          bookmarks: 0,
          comments: 0,
          shares: 0,
          total_reactions: 0,
        },
      };
    }
  }

  // ============ RELATED POSTS METHODS ============

  async getRelatedPosts(postId, limit = 5) {
    try {
      const config = this.getRequestConfig({ params: { limit } });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/related/${postId}`, config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch related posts",
        message: error.message,
        data: [],
      };
    }
  }

  async getPostsByMonth(year, month, params = {}) {
    try {
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/month/${year}/${month}`, config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatPostForUI.bind(this))
          : this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch posts by month",
        message: error.message,
        data: [],
      };
    }
  }

  async getPostCommentsList(postId, includeReplies = true) {
    try {
      const config = this.getRequestConfig({
        params: { include_replies: includeReplies },
      });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/${postId}/comments/list`, config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatCommentForUI.bind(this))
          : this.formatCommentForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch post comments list",
        message: error.message,
        data: [],
      };
    }
  }

  // ============ PUBLIC CATEGORIES & TAGS ENDPOINTS ============

  async listCategories(params = {}) {
    try {
      const queryParams = {
        ...params,
        include_deleted: false,
      };

      const config = this.getRequestConfig({ params: queryParams });
      const response = await this.withRetry(() =>
        apiService.get("/blog/categories", config),
      );

      // Ensure we return a consistent format
      if (response && response.data) {
        // If response.data has success property, return that structure
        if (response.data.success !== undefined) {
          return response.data;
        }

        // If response.data is already an array, wrap it in a success response
        if (Array.isArray(response.data)) {
          // Filter out any deleted records just in case
          const filteredData = response.data.filter((cat) => !cat.deleted_at);
          return {
            success: true,
            data: filteredData,
          };
        }

        // If response.data has data property that's an array
        if (response.data.data && Array.isArray(response.data.data)) {
          // Filter out any deleted records just in case
          const filteredData = response.data.data.filter(
            (cat) => !cat.deleted_at,
          );
          return {
            success: true,
            data: filteredData,
            meta: response.data.meta,
          };
        }
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch categories",
        message: error.message,
        data: [],
      };
    }
  }

  async getCategoryTree() {
    try {
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/blog/categories/tree", config),
      );

      const formatCategoryTree = (categories) => {
        return categories.map((category) => ({
          ...this.formatCategoryForUI(category),
          children: category.children
            ? formatCategoryTree(category.children)
            : [],
        }));
      };

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? formatCategoryTree(response.data)
          : formatCategoryTree([response.data]);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category tree",
        message: error.message,
        data: [],
      };
    }
  }

  async getCategoryById(id) {
    try {
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/blog/categories/${id}`, config),
      );

      if (response.data) {
        response.data = this.formatCategoryForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category",
        message: error.message,
      };
    }
  }

  async getCategoryBySlug(slug) {
    try {
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(
          `/blog/categories/slug/${encodeURIComponent(slug)}`,
          config,
        ),
      );

      if (response.data) {
        response.data = this.formatCategoryForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch category by slug",
        message: error.message,
      };
    }
  }

  async listTags(params = {}) {
    try {
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get("/blog/tags", config),
      );

      // Handle paginated response
      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatTagForUI.bind(this),
        );
        return this.parsePaginatedResponse(response);
      } else if (Array.isArray(response.data)) {
        response.data = response.data.map(this.formatTagForUI.bind(this));
        return {
          data: response.data,
          meta: { pagination: { total: response.data.length } },
        };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch tags",
        message: error.message,
        data: [],
        meta: { pagination: { total: 0 } },
      };
    }
  }

  async getPopularTags(limit = 20) {
    try {
      const config = this.getRequestConfig({ params: { limit } });

      const response = await this.withRetry(() =>
        apiService.get("/blog/tags/popular", config),
      );

      if (response.data) {
        response.data = Array.isArray(response.data)
          ? response.data.map(this.formatTagForUI.bind(this))
          : this.formatTagForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch popular tags",
        message: error.message,
        data: [],
      };
    }
  }

  async getTagBySlug(slug) {
    try {
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/blog/tags/slug/${encodeURIComponent(slug)}`, config),
      );

      if (response.data) {
        response.data = this.formatTagForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch tag by slug",
        message: error.message,
      };
    }
  }

  // ============ POST INTERACTIONS (PUBLIC) ============

  async recordPostView(postId, options = {}) {
    try {
      const sessionId = this.generateSessionId();
      const userId = this.getUserId();

      const config = this.getPublicRequestConfig();

      const viewData = {
        session_id: sessionId,
        user_id: userId,
        user_agent: navigator.userAgent,
        skip_language: true,
        ...options,
      };

      const response = await this.withRetry(() =>
        apiService.post(`/blog/posts/${postId}/view`, viewData, config),
      );

      return response;
    } catch (error) {
      return {
        success: true,
        data: { is_unique: true, view_count: 0 },
        message: "View recorded locally",
        timestamp: new Date().toISOString(),
      };
    }
  }

  async updateViewDuration(postId, durationSeconds) {
    try {
      const sessionId = this.generateSessionId();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/posts/${postId}/view/duration`,
          { session_id: sessionId, duration_seconds: durationSeconds },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update view duration",
        message: error.message,
      };
    }
  }

  async updateScrollDepth(postId, scrollDepth) {
    try {
      const sessionId = this.generateSessionId();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/posts/${postId}/view/scroll`,
          { session_id: sessionId, scroll_depth: scrollDepth },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update scroll depth",
        message: error.message,
      };
    }
  }

  async togglePostLike(postId) {
    try {
      const sessionId = this.generateSessionId();
      const userId = this.getUserId();
      const userAgent = navigator.userAgent;

      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/posts/${postId}/like`,
          {
            session_id: sessionId,
            user_id: userId,
            user_agent: userAgent,
          },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle like",
        message: error.message,
        data: { liked: false, count: 0 },
      };
    }
  }

  async togglePostClap(postId, clapCount = 1) {
    try {
      const sessionId = this.generateSessionId();
      const userId = this.getUserId();

      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/posts/${postId}/claps`,
          {
            session_id: sessionId,
            user_id: userId,
            clap_count: clapCount,
          },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle clap",
        message: error.message,
        data: { total_claps: 0, user_claps: 0, claped: false },
      };
    }
  }

  async updatePostShares(postId, platform, count) {
    try {
      const config = this.getPublicRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/posts/${postId}/shares`,
          { platform, count },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update post shares",
        message: error.message,
        data: { total_shares: 0, platform_shares: 0 },
      };
    }
  }

  async getPostComments(postId, params = {}) {
    try {
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get(`/blog/posts/${postId}/comments`, config),
      );

      // Handle paginated response
      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatCommentForUI.bind(this),
        );
        return this.parsePaginatedResponse(response);
      } else if (response.data?.comments) {
        response.data.comments = response.data.comments.map(
          this.formatCommentForUI.bind(this),
        );
      } else if (Array.isArray(response.data)) {
        response.data = response.data.map(this.formatCommentForUI.bind(this));
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch post comments",
        message: error.message,
        data: [],
        meta: { pagination: { total: 0 } },
      };
    }
  }

  // ============ PUBLIC COMMENT CREATION ============

  async createComment(data) {
    try {
      const validation = this.validateCommentData(data);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      const hasAvatar = data.author_avatar instanceof File;
      const sessionId = this.generateSessionId();
      const userId = this.getUserId();

      // Check if user is authenticated
      const isAuthenticated = this.isAuthenticated();
      const token = this.getAuthTokenFromCookie();

      let requestData;
      let config;

      if (hasAvatar) {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
          if (key === "author_avatar" && data[key] instanceof File) {
            formData.append("author_avatar", data[key]);
          } else {
            const value =
              data[key] !== undefined && data[key] !== null
                ? String(data[key])
                : "";
            formData.append(key, value);
          }
        });

        // Add tracking info
        formData.append("user_agent", navigator.userAgent);
        formData.append("guest_session_id", sessionId);
        if (userId) formData.append("created_by", userId);

        requestData = formData;
        config = {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        };
      } else {
        // For JSON requests - start with the original data
        requestData = {
          content: data.content,
          post_id: data.post_id,
          parent_id: data.parent_id || null,
          rating: data.rating || null,
          user_agent: navigator.userAgent,
          guest_session_id: sessionId,
          referrer: document.referrer || null,
        };

        // Add author fields based on authentication status
        if (!isAuthenticated) {
          // Guest logic
          requestData.author_name = data.author_name;
          requestData.author_email = data.author_email;
          requestData.author_website = data.author_website || null;
          config = this.getPublicRequestConfig();
        } else {
          // For authenticated users
          const userEmail = this.getUserEmail();
          const userName = this.getUserName();

          // Add author fields to the requestData
          requestData.author_name = userName || "Admin User";
          requestData.author_email = userEmail;
          requestData.author_website = null;

          config = this.getRequestConfig();
        }
      }

      const response = await this.withRetry(() =>
        apiService.instance.post("/blog/comments", requestData, config),
      );

      // Format the response consistently
      if (response.data) {
        if (response.data.success === false) {
          return {
            success: false,
            error: response.data.error || "Failed to create comment",
            message:
              response.data.message ||
              "Please check your input data and try again",
            data: null,
          };
        }

        const formattedData = this.formatCommentForUI(response.data);
        return {
          success: true,
          data: formattedData,
          message: "Comment added successfully",
        };
      }

      return response;
    } catch (error) {
      if (error.response) {
        const errorData = error.response.data;
        return {
          success: false,
          error: errorData.error || "Failed to create comment",
          message: errorData.message || error.message,
          status: error.response.status,
          data: null,
        };
      }

      return {
        success: false,
        error: "Failed to create comment",
        message: error.message,
        data: null,
      };
    }
  }

  // ============ ADMIN POST MANAGEMENT ============

  async getAllPosts(params = {}) {
    try {
      this.ensureAuthenticated();

      const processedParams = { ...params };
      if (params.exclude_ids && Array.isArray(params.exclude_ids)) {
        processedParams.exclude_ids = params.exclude_ids.join(",");
      }
      if (params.date_range && typeof params.date_range === "object") {
        processedParams.date_range = JSON.stringify(params.date_range);
      }
      if (params.status && Array.isArray(params.status)) {
        processedParams.status = params.status;
      }

      const config = this.getRequestConfig({ params: processedParams });

      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/posts", config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatPostForUI.bind(this),
        );
      } else if (response.data?.posts || response.data?.items) {
        const posts = response.data.posts || response.data.items;
        response.data = {
          ...response.data,
          posts: posts.map(this.formatPostForUI.bind(this)),
        };
      } else if (Array.isArray(response.data)) {
        response.data = response.data.map(this.formatPostForUI.bind(this));
      }

      return this.parsePaginatedResponse(response);
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch all posts",
        message: error.message,
        status: error.response?.status,
        data: [],
        meta: { pagination: { total: 0 } },
      };
    }
  }

  async getPostById(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/blog/admin/posts/${id}`, config),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch post",
        message: error.message,
      };
    }
  }

  async createPost(data) {
    try {
      this.ensureAuthenticated();
      const token = this.getAuthTokenFromCookie();

      // Direct axios call for FormData
      const response = await apiService.instance.post(
        "/blog/admin/posts",
        data,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          // Prevent axios from setting Content-Type
          transformRequest: [
            (data, headers) => {
              // Remove any Content-Type that axios might add
              delete headers["Content-Type"];
              return data;
            },
          ],
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updatePost(id, data) {
    try {
      this.ensureAuthenticated();
      const token = this.getAuthTokenFromCookie();

      let requestData;
      let config;

      if (data instanceof FormData) {
        // Already FormData - use directly
        requestData = data;

        // Check if file exists in FormData
        const fileCheck = data.get("featured_image");
      } else {
        // Convert to FormData
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
          const value = data[key];

          if (value instanceof File || value instanceof Blob) {
            formData.append(key, value);
          } else if (key === "tags" && Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (key === "related_post_ids" && Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (key === "custom_fields" && typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else if (value !== undefined && value !== null) {
            formData.append(key, String(value));
          }
        });

        requestData = formData;
      }

      config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await apiService.instance.put(
        `/blog/admin/posts/${id}`,
        requestData,
        config,
      );

      return response.data;
    } catch (error) {
      if (
        error.response?.data?.error?.includes("modified by another user") &&
        !isForceUpdate
      ) {
        return {
          success: false,
          error: "Post has been modified by another user",
          message: "Version conflict detected",
          status: error.response.status,
          data: error.response.data,
        };
      }

      // For other errors or force update failures, return the original error
      let errorMessage = "Failed to update post";
      let errorData = null;

      if (error.response) {
        errorData = error.response.data;
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
        status: error.response?.status,
        data: errorData,
      };
    }
  }

  async deletePost(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.delete(`/blog/admin/posts/${id}`, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete post",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async publishPost(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(`/blog/admin/posts/${id}/publish`, {}, config),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to publish post",
        message: error.message,
      };
    }
  }

  async schedulePost(id, scheduledAt) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/admin/posts/${id}/schedule`,
          { scheduled_at: scheduledAt },
          config,
        ),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to schedule post",
        message: error.message,
      };
    }
  }

  async duplicatePost(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(`/blog/admin/posts/${id}/duplicate`, {}, config),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to duplicate post",
        message: error.message,
      };
    }
  }

  async toggleFeaturedStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/blog/admin/posts/${id}/featured`, {}, config),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle featured status",
        message: error.message,
      };
    }
  }

  async togglePinnedStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/blog/admin/posts/${id}/pinned`, {}, config),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle pinned status",
        message: error.message,
      };
    }
  }

  async toggleCommentableStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/blog/admin/posts/${id}/commentable`, {}, config),
      );

      if (response.data) {
        response.data = this.formatPostForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle commentable status",
        message: error.message,
      };
    }
  }

  // ============ ADMIN CATEGORY MANAGEMENT ============

  /**
   * Create a new category (admin only)
   * @param {Object|FormData} data - Category data
   * @returns {Promise<Object>} API response
   */
  async createCategory(data) {
    try {
      this.ensureAuthenticated();

      // If data is FormData, send it directly
      if (data instanceof FormData) {
        const token = this.getAuthTokenFromCookie();
        const config = {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await apiService.instance.post(
          "/blog/admin/categories",
          data,
          config,
        );

        return {
          success: true,
          data: response.data,
          message: "Category created successfully",
        };
      }

      // Handle plain object without files
      const apiData = this.formatCategoryForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/categories", apiData, config),
      );

      return {
        success: true,
        data: response.data,
        message: "Category created successfully",
      };
    } catch (error) {
      let errorMessage = "Failed to create category";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  /**
   * Update a category (admin only)
   * @param {string} id - Category ID
   * @param {Object|FormData} data - Category data
   * @returns {Promise<Object>} API response
   */
  async updateCategory(id, data) {
    try {
      this.ensureAuthenticated();

      if (data instanceof FormData) {
        const token = this.getAuthTokenFromCookie();
        const config = {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await apiService.instance.put(
          `/blog/admin/categories/${id}`,
          data,
          config,
        );

        // Return a consistent response format
        return {
          success: true,
          data: response.data,
          message: "Category updated successfully",
        };
      }

      const apiData = this.formatCategoryForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/blog/admin/categories/${id}`, apiData, config),
      );

      return {
        success: true,
        data: response.data,
        message: "Category updated successfully",
      };
    } catch (error) {
      let errorMessage = "Failed to update category";
      let errorData = null;

      if (error.response) {
        errorData = error.response.data;
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
        status: error.response?.status,
        data: errorData,
      };
    }
  }

  async deleteCategory(id) {
    try {
      this.ensureAuthenticated();

      const config = this.getRequestConfig();

      const response = await apiService.delete(
        `/blog/admin/categories/${id}`,
        config,
      );

      return {
        success: true,
        data: response.data,
        message: response.data?.message || "Category deleted successfully",
      };
    } catch (error) {
      let errorMessage = "Failed to delete category";
      let errorDetails = null;
      let canDeactivate = false;

      if (error.response) {
        errorDetails = error.response.data;
        errorMessage = `Server error (${error.response.status}): `;

        if (typeof error.response.data === "string") {
          errorMessage += error.response.data;
        } else if (error.response.data?.message) {
          errorMessage += error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage += error.response.data.error;
        } else {
          errorMessage += "Internal server error";
        }

        if (
          error.response.status === 409 ||
          error.response.status === 422 ||
          error.response.status === 500
        ) {
          const responseData = error.response.data;
          const errorString = JSON.stringify(responseData || {}).toLowerCase();

          if (
            errorString.includes("foreign key") ||
            errorString.includes("constraint") ||
            errorString.includes("posts") ||
            errorString.includes("articles") ||
            errorString.includes("in use") ||
            errorString.includes("used by")
          ) {
            canDeactivate = true;
            errorMessage =
              "لا يمكن حذف التصنيف لأنه يحتوي على مقالات مرتبطة به";
          }
        }
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = error.message || "Failed to delete category";
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
        details: errorDetails,
        status: error.response?.status,
        statusText: error.response?.statusText,
        canDeactivate: canDeactivate,
        timestamp: new Date().toISOString(),
      };
    }
  }

  async softDeleteCategory(id) {
    try {
      this.ensureAuthenticated();

      const updateData = {
        is_active: false,
        is_nav_visible: false,
        show_in_homepage: false,
        deleted_at: new Date().toISOString(),
      };

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/blog/admin/categories/${id}`, updateData, config),
      );

      return {
        success: true,
        data: response.data,
        message: "Category deactivated successfully",
        softDelete: true,
      };
    } catch (error) {
      return {
        success: false,
        error: "Failed to deactivate category",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ ADMIN TAG MANAGEMENT ============

  async createTag(data) {
    try {
      this.ensureAuthenticated();

      const validation = this.validateTagData(data);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      const apiData = this.formatTagForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/blog/admin/tags", apiData, config),
      );

      if (response.data) {
        response.data = this.formatTagForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to create tag",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ ADMIN COMMENT MANAGEMENT ============

  /**
   * Get comment UUID from short ID
   * @param {string} shortId - Short ID
   * @returns {Promise<string>} UUID
   */
  async getCommentUUID(shortId) {
    try {
      // If no ID provided, return null
      if (!shortId) {
        return null;
      }

      if (this.isValidUUID(shortId)) {
        return shortId;
      }

      const config = this.getRequestConfig();
      const response = await apiService.get(
        `/blog/admin/comments/${shortId}`,
        config,
      );

      if (response.data && response.data.id) {
        if (this.isValidUUID(response.data.id)) {
          return response.data.id;
        }
        if (response.data.uuid && this.isValidUUID(response.data.uuid)) {
          return response.data.uuid;
        }
      }

      return shortId;
    } catch (error) {
      return shortId;
    }
  }

  async getComments(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/comments", config),
      );

      // Handle paginated response
      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatCommentForUI.bind(this),
        );
        return this.parsePaginatedResponse(response);
      } else if (response.data?.comments) {
        response.data.comments = response.data.comments.map(
          this.formatCommentForUI.bind(this),
        );
      } else if (Array.isArray(response.data)) {
        response.data = response.data.map(this.formatCommentForUI.bind(this));
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch comments",
        message: error.message,
        data: [],
        meta: { pagination: { total: 0 } },
      };
    }
  }

  async updateComment(id, data) {
    try {
      this.ensureAuthenticated();

      const validation = this.validateCommentData(data);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      const apiData = this.formatCommentForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/blog/admin/comments/${id}`, apiData, config),
      );

      if (response.data) {
        response.data = this.formatCommentForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update comment",
        message: error.message,
      };
    }
  }

  async deleteComment(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.delete(`/blog/admin/comments/${id}`, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete comment",
        message: error.message,
      };
    }
  }

  async moderateComment(id, moderationData = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/admin/comments/${id}/moderate`,
          moderationData,
          config,
        ),
      );

      if (response.data) {
        response.data = this.formatCommentForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to moderate comment",
        message: error.message,
      };
    }
  }

  /**
   * Approve a comment (admin only)
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} API response
   */
  async approveComment(commentId) {
    try {
      this.ensureAuthenticated();

      // Ensure commentId is not undefined
      if (!commentId) {
        return {
          success: false,
          error: "Comment ID is required",
          message: "Comment ID is required",
        };
      }

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/admin/comments/${commentId}/approve`,
          {},
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to approve comment",
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  /**
   * Mark a comment as spam (admin only)
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} API response
   */
  async markCommentAsSpam(commentId) {
    try {
      this.ensureAuthenticated();

      // Ensure commentId is not undefined
      if (!commentId) {
        return {
          success: false,
          error: "Comment ID is required",
          message: "Comment ID is required",
        };
      }

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(`/blog/admin/comments/${commentId}/spam`, {}, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to mark comment as spam",
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  async markCommentAsSpam(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(`/blog/admin/comments/${id}/spam`, {}, config),
      );

      if (response.data) {
        response.data = this.formatCommentForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to mark comment as spam",
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  // ============ STATISTICS & ANALYTICS ============

  async getBlogStats() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/stats", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch blog statistics",
        message: error.message,
      };
    }
  }

  async getAnalytics(params = {}) {
    try {
      this.ensureAuthenticated();

      const processedParams = { ...params };
      if (params.date_range && typeof params.date_range === "object") {
        const formatted = this.formatDateRange(params.date_range);
        if (formatted) {
          processedParams.start_date = formatted.from;
          processedParams.end_date = formatted.to;
          delete processedParams.date_range;
        }
      }

      const config = this.getRequestConfig({ params: processedParams });

      const response = await this.withRetry(() =>
        apiService.get("/blog/admin/analytics", config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch analytics",
        message: error.message,
      };
    }
  }

  // ============ BOOKMARKS ============

  async toggleBookmark(postId, folder, notes) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/blog/posts/${postId}/bookmark`,
          { folder, notes },
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle bookmark",
        message: error.message,
        data: { bookmarked: false, count: 0 },
      };
    }
  }

  // ============ HELPER METHODS ============

  /**
   * Detect device type from user agent
   * @returns {string} Device type
   */
  detectDeviceType() {
    const userAgent = navigator.userAgent || "";
    const ua = userAgent.toLowerCase();

    if (ua.includes("bot") || ua.includes("crawler") || ua.includes("spider")) {
      return "bot";
    }

    if (
      ua.includes("mobile") ||
      ua.includes("android") ||
      ua.includes("iphone")
    ) {
      return "mobile";
    }

    if (ua.includes("tablet") || ua.includes("ipad")) {
      return "tablet";
    }

    return "desktop";
  }

  /**
   * Detect browser from user agent
   * @returns {string} Browser name
   */
  detectBrowser() {
    const userAgent = navigator.userAgent || "";
    const ua = userAgent.toLowerCase();

    if (ua.includes("chrome")) return "Chrome";
    if (ua.includes("firefox")) return "Firefox";
    if (ua.includes("safari") && !ua.includes("chrome")) return "Safari";
    if (ua.includes("edge")) return "Edge";
    if (ua.includes("opera")) return "Opera";
    if (ua.includes("msie") || ua.includes("trident"))
      return "Internet Explorer";

    return "Unknown";
  }

  /**
   * Detect OS from user agent
   * @returns {string} OS name
   */
  detectOS() {
    const userAgent = navigator.userAgent || "";
    const ua = userAgent.toLowerCase();

    if (ua.includes("windows")) return "Windows";
    if (ua.includes("mac os")) return "macOS";
    if (ua.includes("linux")) return "Linux";
    if (ua.includes("android")) return "Android";
    if (ua.includes("iphone") || ua.includes("ipad")) return "iOS";

    return "Unknown";
  }

  /**
   * Detect referrer source from document referrer
   * @returns {string|null} Referrer source
   */
  detectReferrerSource() {
    const referrer = document.referrer;
    if (!referrer) return "direct";

    if (
      referrer.includes("google.com") ||
      referrer.includes("bing.com") ||
      referrer.includes("yahoo.com") ||
      referrer.includes("baidu.com")
    ) {
      return "search";
    }

    if (
      referrer.includes("facebook.com") ||
      referrer.includes("twitter.com") ||
      referrer.includes("linkedin.com") ||
      referrer.includes("instagram.com") ||
      referrer.includes("pinterest.com")
    ) {
      return "social";
    }

    if (
      referrer.includes("mail.google.com") ||
      referrer.includes("outlook") ||
      referrer.includes("yahoo.com/mail")
    ) {
      return "email";
    }

    if (referrer.includes(window.location.hostname)) {
      return "internal";
    }

    return "other";
  }

  /**
   * Get URL parameter by name
   * @param {string} name - Parameter name
   * @returns {string|null} Parameter value
   */
  getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  /**
   * Generate session ID for tracking
   * @returns {string} Session ID
   */
  generateSessionId() {
    let sessionId = localStorage.getItem("blog_session_id");
    if (!sessionId) {
      sessionId =
        "blog_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("blog_session_id", sessionId);
    }
    return sessionId;
  }

  /**
   * Get user ID from local storage
   * @returns {string|null} User ID or null
   */
  getUserId() {
    try {
      const userStr = localStorage.getItem("admin_user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || null;
      }
    } catch (error) {
      console.warn("Error getting user ID:", error);
    }
    return null;
  }

  /**
   * Parse paginated response
   * @param {Object} response - API response
   * @returns {Object} Parsed response with data and pagination
   */
  parsePaginatedResponse(response) {
    if (!response || !response.data) {
      return {
        data: [],
        meta: { pagination: { total: 0 } },
      };
    }

    if (response.data.data && response.data.meta) {
      return {
        data: response.data.data,
        meta: response.data.meta,
      };
    } else if (Array.isArray(response.data)) {
      return {
        data: response.data,
        meta: {
          pagination: {
            total: response.data.length,
            page: 1,
            limit: response.data.length,
            total_pages: 1,
            has_next: false,
            has_prev: false,
          },
        },
      };
    } else {
      return response.data;
    }
  }

  /**
   * Generate a slug from a string
   * @param {string} text - Text to convert to slug
   * @returns {string} Generated slug
   */
  generateSlug(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /**
   * Calculate reading time from content
   * @param {string} content - Post content
   * @returns {number} Reading time in minutes
   */
  calculateReadingTime(content) {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  }

  /**
   * Extract excerpt from content
   * @param {string} content - Post content
   * @param {number} length - Desired length in characters
   * @returns {string} Excerpt
   */
  extractExcerpt(content, length = 160) {
    const plainText = content.replace(/<[^>]*>/g, "");
    return plainText.length > length
      ? plainText.substring(0, length) + "..."
      : plainText;
  }

  /**
   * Extract ID from object
   * @param {Object} obj - Object with ID
   * @returns {string|null} Extracted ID
   */
  extractIdFromObject(obj) {
    return obj?.uuid || obj?.id || obj?._id || null;
  }

  /**
   * Check if it's a valid UUID
   * @param {string} id - ID to check
   * @returns {boolean} True if valid UUID
   */
  isValidUUID(id) {
    if (!id) return false;
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }

  /**
   * Check if it's a short ID
   * @param {string} id - ID to check
   * @returns {boolean} True if short ID
   */
  isShortId(id) {
    if (!id) return false;
    return id.length <= 32 && /^[a-zA-Z0-9\-_]+$/.test(id);
  }
}

export const blogService = new BlogService();
